import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { fetchActiveCycle } from '../../services/reviewCycleService';
import { fetchFormsForReviewee } from '../../services/reviewFormService';
import { listInvitationsForCycle } from '../../services/reviewInvitationService';
import { getCycleDisplayLabel } from '../../services/cycleUtils';
import StatCard from '../../components/dashboard/StatCard';
import StatusBadge from '../../components/dashboard/StatusBadge';
import type { ReviewCycle, ReviewForm, ReviewWorkflowStatus } from '../../types';
import type { ReviewInvitation } from '../../types/magicLink';

interface DirectReport {
  id: string;
  name: string;
  email?: string;
  jobTitle?: string;
}

interface MyTodoItem {
  kind: 'manager_review' | 'magic_link_invite';
  label: string;
  sub: string;
  to: string;
  urgent: boolean;
}

interface PanelTally { total: number; submitted: number; opened: number; pending: number; }

const ManagerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState<ReviewCycle | null>(null);
  const [myForm, setMyForm] = useState<ReviewForm | null>(null);
  const [directReports, setDirectReports] = useState<DirectReport[]>([]);
  const [reportForms, setReportForms] = useState<ReviewForm[]>([]);
  const [myInvitations, setMyInvitations] = useState<ReviewInvitation[]>([]);
  const [allCycleInvitations, setAllCycleInvitations] = useState<ReviewInvitation[]>([]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    void (async () => {
      try {
        const c = await fetchActiveCycle();
        if (!alive) return;
        if (!c) { setLoading(false); return; }
        setCycle(c);

        const myForms = await fetchFormsForReviewee(user.id);
        if (!alive) return;
        setMyForm(myForms.find(f => f.cycleId === c.id) ?? null);

        const drSnap = await getDocs(query(
          collection(db, 'users'),
          where('directManagerId', '==', user.id),
          where('isActive', '==', true),
        ));
        if (!alive) return;
        const drs: DirectReport[] = drSnap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            name: String(data.name ?? data.displayName ?? ''),
            email: data.email,
            jobTitle: data.jobTitle,
          };
        }).filter(r => r.name);
        setDirectReports(drs);

        if (drs.length > 0) {
          const drFormsSnap = await getDocs(query(
            collection(db, 'reviewForms'),
            where('cycleId', '==', c.id),
            where('revieweeId', 'in', drs.map(r => r.id).slice(0, 10)),
          ));
          if (!alive) return;
          setReportForms(drFormsSnap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewForm));
        }

        const allInvs = await listInvitationsForCycle(c.id);
        if (!alive) return;
        setAllCycleInvitations(allInvs);
        const myEmail = (user.email ?? '').toLowerCase();
        const mineAsReviewer = allInvs.filter(i =>
          i.reviewerId === user.id ||
          i.reviewerEmail?.toLowerCase() === myEmail
        ).filter(i => i.status !== 'submitted' && i.status !== 'expired')
         .filter(i => i.revieweeId !== user.id);
        setMyInvitations(mineAsReviewer);

        setLoading(false);
      } catch (e) {
        console.error('[ManagerDashboard] load failed:', e);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // ─── Derived ─────────────────────────────────────────────────────────────
  const cycleLabel = cycle ? getCycleDisplayLabel(cycle.period, cycle.year) : '—';

  const daysToLock = useMemo(() => {
    const lockTs = cycle?.lockDate as { toDate?: () => Date } | undefined;
    const d = lockTs?.toDate?.();
    return d ? Math.ceil((d.getTime() - Date.now()) / 86400000) : null;
  }, [cycle]);

  const panelByReviewee = useMemo(() => {
    const map = new Map<string, PanelTally>();
    for (const inv of allCycleInvitations) {
      const cur = map.get(inv.revieweeId) ?? { total: 0, submitted: 0, opened: 0, pending: 0 };
      cur.total += 1;
      if (inv.status === 'submitted') cur.submitted += 1;
      else if (inv.status === 'opened' || inv.status === 'in_progress') cur.opened += 1;
      else if (inv.status === 'sent') cur.pending += 1;
      map.set(inv.revieweeId, cur);
    }
    return map;
  }, [allCycleInvitations]);

  const todos = useMemo<MyTodoItem[]>(() => {
    const items: MyTodoItem[] = [];
    for (const inv of myInvitations) {
      const expiresAt = (inv.expiresAt as { toDate?: () => Date }).toDate?.();
      const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / 86400000) : null;
      items.push({
        kind: 'magic_link_invite',
        label: `Review ${inv.revieweeName}`,
        sub: `${inv.reviewerRole.replace('_', ' ')}${daysLeft !== null ? ` · expires in ${daysLeft}d` : ''}`,
        to: `/r/${inv.token}`,
        urgent: daysLeft !== null && daysLeft <= 3,
      });
    }
    for (const form of reportForms) {
      if (form.status === 'manager_pending' || form.status === '360_collected') {
        items.push({
          kind: 'manager_review',
          label: `Manager review for ${form.revieweeName}`,
          sub: 'Self-review submitted, awaiting your assessment',
          to: `/manager-review/${form.id}`,
          urgent: false,
        });
      }
    }
    return items;
  }, [myInvitations, reportForms]);

  if (loading) return <div className="p-8 text-stone-500 text-sm">Loading manager dashboard…</div>;
  if (!cycle)  return (
    <div className="max-w-3xl mx-auto p-8">
      <Greeting userName={user?.name} />
      <div className="bg-stone-50 border border-stone-200 rounded-xl p-8 text-center text-stone-600">
        No active review cycle.
      </div>
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <Greeting userName={user?.name} cycleLabel={cycleLabel} daysToLock={daysToLock} />

      {/* Your own self-review */}
      {myForm && (
        <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Your own review</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm font-medium text-stone-900">Self-Review</span>
              <StatusBadge status={myForm.status} />
            </div>
          </div>
          <Link to="/self-review" className="text-sm text-blue-600 hover:underline shrink-0">Open →</Link>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Direct reports" value={directReports.length} sub="active team members" tone="neutral" />
        <StatCard
          label="To-do"
          value={todos.length}
          sub={todos.some(t => t.urgent) ? 'with urgent' : todos.length > 0 ? 'reviews to fill' : 'all caught up'}
          tone={todos.some(t => t.urgent) ? 'warn' : todos.length > 0 ? 'info' : 'ok'}
        />
        <StatCard
          label="In review"
          value={reportForms.filter(f => !['approved','signed_off'].includes(f.status)).length}
          sub="reviews in progress"
        />
        <StatCard
          label="Completed"
          value={reportForms.filter(f => ['approved','signed_off'].includes(f.status)).length}
          sub="signed off"
          tone="ok"
        />
      </div>

      {/* My to-do */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-stone-900 mb-3">My to-do</h2>
        {todos.length === 0 ? (
          <div className="text-center text-stone-500 py-6 text-sm">
            <span className="material-symbols-rounded text-emerald-500 block mx-auto mb-1" style={{ fontSize: 32 }} aria-hidden="true">
              task_alt
            </span>
            All caught up — no reviews waiting on you.
          </div>
        ) : (
          <div className="space-y-2">
            {todos.map((t, i) => (
              <Link
                key={`${t.kind}-${i}`}
                to={t.to}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition hover:border-stone-400 ${t.urgent ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-200'}`}
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${t.kind === 'magic_link_invite' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}`}>
                    <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">
                      {t.kind === 'magic_link_invite' ? 'link' : 'rate_review'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{t.label}</div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      {t.urgent && <span className="text-red-700 font-semibold">URGENT · </span>}
                      {t.sub}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-blue-600 shrink-0">Open →</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Team — review status */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold text-stone-900">My team — review status</h2>
          <span className="text-[11px] text-stone-500">{directReports.length} {directReports.length === 1 ? 'member' : 'members'}</span>
        </div>
        {directReports.length === 0 ? (
          <div className="text-stone-500 text-sm py-6 text-center">No direct reports assigned yet.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {directReports.map(dr => {
              const form = reportForms.find(f => f.revieweeId === dr.id);
              const panel = panelByReviewee.get(dr.id);
              return (
                <TeamRow key={dr.id} report={dr} form={form} panel={panel} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Subcomponents ─────────────────────────────────────────────────────────

const TeamRow: React.FC<{
  report: DirectReport;
  form?: ReviewForm;
  panel?: PanelTally;
}> = ({ report, form, panel }) => {
  const phase = form ? phaseFromStatus(form.status) : null;
  return (
    <div className="flex items-center gap-3 py-3">
      <Avatar name={report.name} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-stone-900 truncate">{report.name}</div>
        <div className="text-[11px] text-stone-500 truncate">{report.jobTitle ?? '—'}</div>
      </div>
      <div className="hidden sm:flex items-center gap-2 shrink-0">
        {form ? (
          <>
            {phase && <PhaseChip phase={phase} />}
            {panel && panel.total > 0 && <PanelProgress tally={panel} />}
          </>
        ) : (
          <span className="text-[11px] text-stone-400 italic">Not enrolled</span>
        )}
      </div>
      <div className="shrink-0">
        {form
          ? <Link to={`/reviews/${form.id}`} className="text-xs font-medium text-blue-600 hover:underline">Monitor →</Link>
          : <span className="text-xs text-stone-300">—</span>}
      </div>
    </div>
  );
};

const Avatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
  return (
    <div className="shrink-0 w-9 h-9 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-[11px] font-semibold text-stone-600">
      {initials || '·'}
    </div>
  );
};

type Phase = 'prep' | 'self' | 'panel' | 'manager' | 'calibration' | 'done';

function phaseFromStatus(s: ReviewWorkflowStatus): Phase {
  switch (s) {
    case 'prep_window':       return 'prep';
    case 'self_pending':
    case 'self_submitted':    return 'self';
    case '360_pending':
    case '360_collected':     return 'panel';
    case 'manager_pending':
    case 'manager_submitted': return 'manager';
    case 'calibration':       return 'calibration';
    case 'approved':
    case 'signed_off':        return 'done';
  }
}

const PHASE_META: Record<Phase, { label: string; cls: string }> = {
  prep:        { label: 'Prep',        cls: 'bg-stone-100  text-stone-700  ring-stone-200' },
  self:        { label: 'Self-Review', cls: 'bg-blue-50    text-blue-700   ring-blue-200' },
  panel:       { label: 'Reviewers',   cls: 'bg-purple-50  text-purple-700 ring-purple-200' },
  manager:     { label: 'Manager',     cls: 'bg-amber-50   text-amber-700  ring-amber-200' },
  calibration: { label: 'Calibration', cls: 'bg-indigo-50  text-indigo-700 ring-indigo-200' },
  done:        { label: 'Done',        cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
};

const PhaseChip: React.FC<{ phase: Phase }> = ({ phase }) => {
  const m = PHASE_META[phase];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${m.cls}`}>
      {m.label}
    </span>
  );
};

const PanelProgress: React.FC<{ tally: PanelTally }> = ({ tally }) => {
  const pct = tally.total === 0 ? 0 : Math.round((tally.submitted / tally.total) * 100);
  const tone = pct >= 80 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-stone-400';
  return (
    <div className="flex items-center gap-2" title={`${tally.submitted}/${tally.total} reviewers submitted`}>
      <div className="w-16 h-1.5 bg-stone-200 rounded-full overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] tabular-nums text-stone-600 w-8 text-right">{tally.submitted}/{tally.total}</span>
    </div>
  );
};

const Greeting: React.FC<{ userName?: string; cycleLabel?: string; daysToLock?: number | null }> = ({ userName, cycleLabel, daysToLock }) => (
  <div className="flex items-end justify-between gap-3 flex-wrap">
    <div>
      <div className="text-[11px] uppercase tracking-wider text-stone-500">Manager Dashboard</div>
      <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
        Hi, {userName?.split(' ')[0] ?? 'there'} 👋
      </h1>
      {cycleLabel && (
        <p className="text-sm text-stone-600 mt-1">Active cycle: <b>{cycleLabel}</b></p>
      )}
    </div>
    {typeof daysToLock === 'number' && (
      <div className={`text-[11px] px-2.5 py-1 rounded-full ring-1 ring-inset ${
        daysToLock <= 3  ? 'bg-red-50    text-red-700    ring-red-200'    :
        daysToLock <= 7  ? 'bg-amber-50  text-amber-700  ring-amber-200'  :
                           'bg-stone-50  text-stone-600  ring-stone-200'
      }`}>
        {daysToLock > 0
          ? `Cycle locks in ${daysToLock}d`
          : daysToLock === 0
            ? 'Locks today'
            : `Locked ${Math.abs(daysToLock)}d ago`}
      </div>
    )}
  </div>
);

export default ManagerDashboard;
